import { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import RegisterBirthdayStep from 'src/components/AzarOnly/Login/Steps/Register/BirthdayStep';
import EthnicityStep from 'src/components/AzarOnly/Login/Steps/Register/EthnicityStep';
import Template from 'src/components/AzarOnly/Login/Steps/Template';
import useRegister from 'src/components/AzarOnly/Login/useRegister';
import Input from 'src/components/Input';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import { socialTokenAtom } from 'src/stores/auth/atoms';
import { phoneNumberLoginEmailAtom, pushPhoneNumberLoginHistoryAtom } from 'src/stores/phoneNumberLogin/atoms';
import { isKmcPhoneToken } from 'src/stores/phoneNumberLogin/kmc';
import { FALLBACK_SIGNUP_SETTINGS, signUpSettingsLoadableAtom } from 'src/stores/register/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { emailReg } from 'src/utils/common';
import { pushEvent } from 'src/utils/event-gateway';
const EmailRow = styled.div`
  margin-top: 32px;
  width: 100%;
`;
const GuideWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
  width: 100%;

  ${({
  theme
}) => theme.screenSize.mobile} {
    gap: 10px;
    margin-top: 32px;
  }
`;
const GuideText = styled.p`
  font-size: 12px;
  line-height: 15px;
  color: ${({
  theme
}) => theme.color.content8};
  word-break: break-word;
`;
const formId = 'email-address-register-step-form';

/** 새로 가입을 위해 이메일을 등록 */
const EmailAddressRegisterStep: FC = () => {
  const {
    t
  } = useTranslation();
  const device = useCssMediaDevice();
  const pushHistory = useSetAtom(pushPhoneNumberLoginHistoryAtom);
  const socialToken = useAtomValue(socialTokenAtom);
  const signUpSettingsLoadable = useAtomValue(signUpSettingsLoadableAtom);
  const signUpSettings = signUpSettingsLoadable.state === 'hasData' ? signUpSettingsLoadable.data : FALLBACK_SIGNUP_SETTINGS;
  const {
    tryRegister
  } = useRegister();
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SCREEN_OPENED__EMAIL_INFO
    });
  }, []);
  const [email, setEmail] = useState('');
  const [phoneNumberLoginEmail, setPhoneNumberLoginEmail] = useAtom(phoneNumberLoginEmailAtom);

  // 가입을 위해 이메일을 등록
  const registerEmail = () => {
    setPhoneNumberLoginEmail({
      email,
      errorMessage: ''
    });
    if (isKmcPhoneToken(socialToken)) {
      // KMC verification already includes birthday and gender, so skip those steps.
      if (signUpSettings.ethnicitySettings) {
        pushHistory(<EthnicityStep />);
        return;
      }
      tryRegister();
      return;
    }
    pushHistory(<RegisterBirthdayStep />);
  };
  const handleInput = (text: string) => {
    setEmail(text);
    if (phoneNumberLoginEmail.errorMessage) {
      setPhoneNumberLoginEmail({
        ...phoneNumberLoginEmail,
        errorMessage: ''
      });
    }
  };
  const canRegisterEmail = emailReg(email) && !phoneNumberLoginEmail.errorMessage;
  return <>
      <Template dataTestId='LoginModal-EmailAddressRegisterStep' eventId='SIGNUP_PHONE_EMAIL_TITLE' title={t('SIGNUP_PHONE_EMAIL_TITLE')} description={t('SIGNUP_PHONE_EMAIL_DESC_RU')} mainButtonProps={{
      form: formId,
      type: 'submit',
      disabled: !canRegisterEmail,
      text: t('SIGNUP__NEXT'),
      color: 'primary',
      'data-testid': 'LoginModal-EmailAddressRegisterStep-next'
    }} data-sentry-element="Template" data-sentry-source-file="EmailAddressRegisterStep.tsx">
        <EmailRow data-sentry-element="EmailRow" data-sentry-source-file="EmailAddressRegisterStep.tsx">
          <form id={formId} onSubmit={e => {
          e.preventDefault();
          if (canRegisterEmail) {
            registerEmail();
          }
        }}>
            <Input type='email' value={email} data-testid='LoginModal-EmailAddressRegisterStep-input' onChangeText={handleInput} label={t('SIGNUP_PHONE_EMAIL_PLACEHOLDER')} errorMessage={phoneNumberLoginEmail.errorMessage} autoFocus={device !== 'mobile'} data-sentry-element="Input" data-sentry-source-file="EmailAddressRegisterStep.tsx" />
          </form>
        </EmailRow>
        <GuideWrap data-sentry-element="GuideWrap" data-sentry-source-file="EmailAddressRegisterStep.tsx">
          <GuideText data-sentry-element="GuideText" data-sentry-source-file="EmailAddressRegisterStep.tsx">{t('SIGNUP_PHONE_NUMBER_GUIDE')}</GuideText>
          <GuideText data-sentry-element="GuideText" data-sentry-source-file="EmailAddressRegisterStep.tsx">{t('SIGNUP_EMAIL_VERIFICATION_NOTICE')}</GuideText>
        </GuideWrap>
      </Template>
    </>;
};
export default EmailAddressRegisterStep;